import { familyContentPolicyAccept } from '@chorepop/core/client';
import { openUrl } from '@tauri-apps/plugin-opener';
import type { Component } from 'solid-js';
import { Show, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  roleCode: 'guardian' | 'child';
  policyVersion: string;
  onAccepted: () => void | Promise<void>;
};

export const FamilyContentRulesScreen: Component<Props> = (props) => {
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const isGuardian = () => props.roleCode === 'guardian';

  const accept = async () => {
    setBusy(true);
    setError(null);
    const result = await familyContentPolicyAccept({
      sessionId: props.sessionId,
      policyKind: isGuardian() ? 'family_terms' : 'child_rules',
      policyVersion: props.policyVersion,
    });
    if (!result.ok) {
      setError(result.error.message);
      setBusy(false);
      return;
    }
    await props.onAccepted();
  };

  return (
    <ActionScreen
      class="action-screen--family-content-rules"
      eyebrow={isGuardian() ? 'Family safety' : 'A quick ChorePop promise'}
      title={isGuardian() ? 'Family Content Rules' : 'Keep ChorePop kind and safe'}
      onBack={() => undefined}
      showBack={false}
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          disabled={busy()}
          onClick={() => void accept()}
        >
          {busy() ? 'Saving…' : isGuardian() ? 'Accept and continue' : 'I understand'}
        </button>
      }
    >
      <section class="form-card family-content-rules-card">
        <Show
          when={isGuardian()}
          fallback={
            <>
              <p>ChorePop is shared with the people in your family.</p>
              <ul>
                <li>Use kind words.</li>
                <li>Do not share passwords, PINs, addresses, or other private details.</li>
                <li>If something feels unsafe or unkind, use Report unsafe content.</li>
              </ul>
              <p class="field-hint">
                Reports go privately to ChorePop. The person you report is not told who sent it.
              </p>
            </>
          }
        >
          <p>
            Family-entered names, notes, chores, routines, goals, and replies are visible to invited
            members of this family. Keep them lawful, respectful, and safe for children.
          </p>
          <ul>
            <li>Do not use threatening, sexual, hateful, harassing, or illegal content.</li>
            <li>
              Do not share passwords, PINs, payment details, or unnecessary private information.
            </li>
            <li>Both children and guardians can privately report family content to ChorePop.</li>
          </ul>
          <button
            type="button"
            class="link-button"
            onClick={() => void openUrl('https://chorepop.com/terms')}
          >
            Read the full Terms of use and Family Content Rules
          </button>
        </Show>
        <p class="field-hint">Rules version {props.policyVersion}</p>
      </section>
      <Show when={error()}>
        {(message) => (
          <p class="form-error" role="alert">
            {message()}
          </p>
        )}
      </Show>
    </ActionScreen>
  );
};
